<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in overflow-y-auto">
    <div class="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col my-auto max-h-[90vh]">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30 sticky top-0 z-10">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Shield class="w-5 h-5 text-brand-600" />
            {{ form.id ? 'Editar Garantía' : 'Registrar Nueva Garantía' }}
          </h2>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="flex-1 p-6 overflow-y-auto custom-scrollbar">
        <form id="collateralForm" @submit.prevent="handleSubmit" class="space-y-6">

          <div class="bg-white dark:bg-brand-900/10 p-5 rounded-xl border border-brand-100 dark:border-brand-800/30">
            <div class="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Crédito a Respaldar</label>
                <select v-model="form.loan_id" required :class="inputClasses">
                  <option value="" disabled>Seleccione un crédito...</option>
                  <option v-for="l in loans" :key="l.id" :value="l.id">
                    CREDITO: {{ l.code }} - {{ l.customer_name || 'Sin Nombre' }} (${{ Number(l.amount).toLocaleString() }} - {{ l.product_name }})
                  </option>
                </select>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div>
                <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Tipo de Garantía</label>
                <select v-model="form.collateral_type" required :class="inputClasses">
                  <option value="HIPOTECARIA">Hipotecaria (Inmuebles)</option>
                  <option value="PRENDARIA">Prendaria (Vehículos/Equipos)</option>
                  <option value="FIDUCIARIA">Fiduciaria (Fiador Solidario)</option>
                </select>
              </div>
            </div>
          </div>

          <!-- SECCIÓN DE FOTOS -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-2">
              <label class="text-xs font-bold text-slate-500 uppercase block">Imagen de Garantía 1</label>
              <div @click="$refs.photo1Input.click()" class="relative border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl h-40 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-all overflow-hidden bg-white dark:bg-slate-800">
                <img v-if="form.photo_url_1" :src="form.photo_url_1" class="w-full h-full object-cover" />
                <template v-else>
                  <Upload class="w-8 h-8 text-slate-400 mb-2" />
                  <span class="text-[10px] font-bold text-slate-500 uppercase">Click para subir foto 1</span>
                </template>
                <div v-if="isUploading1" class="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 class="w-8 h-8 animate-spin text-white" />
                </div>
                <input type="file" ref="photo1Input" class="hidden" accept="image/*" @change="handleImageUpload($event, 'photo_url_1')" />
              </div>
            </div>
            <div class="space-y-2">
              <label class="text-xs font-bold text-slate-500 uppercase block">Imagen de Garantía 2</label>
              <div @click="$refs.photo2Input.click()" class="relative border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl h-40 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 transition-all overflow-hidden bg-white dark:bg-slate-800">
                <img v-if="form.photo_url_2" :src="form.photo_url_2" class="w-full h-full object-cover" />
                <template v-else>
                  <Upload class="w-8 h-8 text-slate-400 mb-2" />
                  <span class="text-[10px] font-bold text-slate-500 uppercase">Click para subir foto 2</span>
                </template>
                <div v-if="isUploading2" class="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 class="w-8 h-8 animate-spin text-white" />
                </div>
                <input type="file" ref="photo2Input" class="hidden" accept="image/*" @change="handleImageUpload($event, 'photo_url_2')" />
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Monto Garantía / Avalúo</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                <input
                  v-model.number="form.guarantee_amount"
                  type="number"
                  step="0.01"
                  required
                  :readonly="form.collateral_type === 'FIDUCIARIA'"
                  :class="[inputClasses, 'pl-8 font-bold text-slate-900 dark:text-white', form.collateral_type === 'FIDUCIARIA' ? 'bg-slate-50 cursor-not-allowed' : '']"
                />
              </div>
            </div>
            <div v-if="form.collateral_type !== 'FIDUCIARIA'">
              <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Monto Facial</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                <input v-model.number="form.face_value" type="number" step="0.01" :class="[inputClasses, 'pl-8']" />
              </div>
            </div>
            <div>
              <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Monto Utilizado</label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                <input v-model.number="form.used_amount" type="number" step="0.01" readonly :class="[inputClasses, 'pl-8 bg-slate-50 opacity-80 cursor-not-allowed']" />
              </div>
            </div>
            <div v-if="form.collateral_type !== 'FIDUCIARIA'">
              <label class="text-xs font-bold text-brand-600 dark:text-brand-400 uppercase mb-1 block">% Cobertura</label>
              <div class="relative">
                <input v-model.number="form.coverage_percentage" type="number" step="0.01" readonly :class="[inputClasses, 'pr-8 font-bold bg-brand-50/30 text-brand-700 dark:text-brand-400']" />
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-brand-500 font-bold">%</span>
              </div>
            </div>
          </div>

          <template v-if="form.collateral_type === 'HIPOTECARIA' || form.collateral_type === 'PRENDARIA'">
            <hr class="border-slate-200 dark:border-slate-700">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Propietario del Bien</label>
                <input v-model="form.owner_name" type="text" :class="inputClasses" />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Fecha Inicio</label>
                  <input v-model="form.start_date" type="date" :class="inputClasses" />
                </div>
                <div>
                  <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Fecha Fin / Venc.</label>
                  <input v-model="form.end_date" type="date" :class="[inputClasses, dateError ? 'border-red-500 text-red-600' : '']" />
                </div>
              </div>
            </div>
            <div class="flex justify-end -mt-4">
               <p v-if="dateError" class="text-[10px] text-red-500 font-bold">{{ dateError }}</p>
            </div>
            <div class="flex justify-end -mt-4">
               <span v-if="form.term_months > 0" class="text-xs font-bold text-brand-600 bg-brand-50 px-2 py-1 rounded">
                 Plazo Calculado: {{ form.term_months }} Meses
               </span>
            </div>
            <div>
              <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Descripción del Bien</label>
              <textarea v-model="form.description" rows="2" :class="inputClasses" placeholder="Dirección del inmueble, marca, modelo..."></textarea>
            </div>
          </template>

          <template v-if="form.collateral_type === 'FIDUCIARIA'">
            <hr class="border-slate-200 dark:border-slate-700">
            <div class="bg-amber-50/50 dark:bg-amber-900/10 p-5 rounded-xl border border-amber-100 dark:border-amber-800/30 relative">
              <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Buscar Fiador Solidario</label>
              <div class="relative">
                <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  v-model="guarantorSearchQuery"
                  @focus="showGuarantorDropdown = true"
                  type="text"
                  placeholder="Escriba nombre o documento del fiador..."
                  :class="[inputClasses, 'pl-9']"
                />
              </div>
              <div v-if="showGuarantorDropdown && filteredGuarantors.length > 0" class="absolute z-50 w-[calc(100%-2.5rem)] mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                <div
                  v-for="c in filteredGuarantors"
                  :key="c.id"
                  @click="selectGuarantor(c)"
                  class="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer border-b border-slate-100 dark:border-slate-700/50 last:border-0"
                >
                  <div class="font-bold text-sm text-slate-900 dark:text-slate-100">{{ c.first_name }} {{ c.last_name }}</div>
                  <div class="text-[10px] font-mono text-slate-500">{{ c.identity_document }}</div>
                </div>
              </div>
              <div v-if="selectedGuarantor" class="mt-3 flex items-center justify-between bg-white dark:bg-slate-900 border border-amber-200 p-2 rounded-lg">
                <div class="flex items-center gap-2">
                  <User class="w-4 h-4 text-amber-600" />
                  <span class="text-xs font-bold text-slate-700 dark:text-slate-200">
                    {{ selectedGuarantor.first_name }} {{ selectedGuarantor.last_name }}
                  </span>
                </div>
                <button @click="clearGuarantor" type="button" class="text-red-400 hover:text-red-600"><X class="w-4 h-4" /></button>
              </div>
            </div>
          </template>

          <div>
            <label class="text-xs font-bold text-slate-500 uppercase mb-1 block">Observaciones</label>
            <textarea v-model="form.observations" rows="2" :class="inputClasses"></textarea>
          </div>
        </form>
      </div>

      <div class="px-6 py-4 bg-slate-50 dark:bg-slate-900/80 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 rounded-b-2xl">
        <button @click="$emit('close')" type="button" class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50">Cancelar</button>
        <button type="submit" form="collateralForm" :disabled="isSaving || dateError !== null || isUploading" class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg flex items-center gap-2 disabled:opacity-50">
          <Loader2 v-if="isSaving || isUploading" class="w-4 h-4 animate-spin" />
          <Save v-else class="w-4 h-4" />
          Guardar Garantía
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';
import { X, Shield, Save, Loader2, Search, User, Upload } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import { useUpload } from '@/composables/useUpload';

const props = defineProps({ show: Boolean, dataToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();
const { uploadFile, isUploading } = useUpload();

const isUploading1 = ref(false);
const isUploading2 = ref(false);
const isSaving = ref(false);
const loans = ref([]);
const customers = ref([]);
const dateError = ref(null);
const guarantorSearchQuery = ref('');
const showGuarantorDropdown = ref(false);
const selectedGuarantor = ref(null);

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-700 dark:text-slate-200";

const form = reactive({
  id: null,
  loan_id: '',
  collateral_type: 'HIPOTECARIA',
  guarantee_amount: 0,
  face_value: 0,
  used_amount: 0,
  coverage_percentage: 0,
  owner_name: '',
  description: '',
  start_date: '',
  end_date: '',
  term_months: 0,
  presentation_number: '',
  presentation_date: '',
  registration_number: '',
  registration_date: '',
  registration_place: '',
  mortgage_type: 'ABIERTA',
  pledge_type: 'VEHICULO',
  pledge_nature: 'SIN_DESPLAZAMIENTO',
  guarantor_id: '',
  photo_url_1: '',
  photo_url_2: '',
  observations: ''
});

onMounted(async () => {
  const user = SecureStorage.getItem('llevo_user');
  if (!user?.company) return;
  try {
    const [resLoans, resCust] = await Promise.all([
      apiClient.get(`/loans?company_id=${user.company}&status=PENDING`),
      apiClient.get(`/customers?company_id=${user.company}`)
    ]);
    loans.value = resLoans.data.data || resLoans.data;
    customers.value = resCust.data.data || resCust.data;
  } catch (e) { console.error(e); }
});

const selectedLoanCustomerId = computed(() => {
  const selectedLoan = loans.value.find(l => String(l.id) === String(form.loan_id));
  return selectedLoan ? Number(selectedLoan.customer_id) : null;
});

const filteredGuarantors = computed(() => {
  let list = Array.isArray(customers.value) ? customers.value : [];

  // Filtro de exclusión blindado con Number()
  if (selectedLoanCustomerId.value) {
    list = list.filter(c => Number(c.id) !== Number(selectedLoanCustomerId.value));
  }

  if (!guarantorSearchQuery.value) return list.slice(0, 10);

  const q = guarantorSearchQuery.value.toLowerCase();
  return list.filter(c =>
    c.first_name?.toLowerCase().includes(q) ||
    c.last_name?.toLowerCase().includes(q) ||
    c.identity_document?.includes(q)
  );
});

const selectGuarantor = (c) => {
  selectedGuarantor.value = c;
  form.guarantor_id = c.id;
  guarantorSearchQuery.value = '';
  showGuarantorDropdown.value = false;
};

const clearGuarantor = () => {
  selectedGuarantor.value = null;
  form.guarantor_id = '';
};

const handleImageUpload = async (event, field) => {
  const file = event.target.files[0];
  if (!file) return;
  field === 'photo_url_1' ? isUploading1.value = true : isUploading2.value = true;
  try {
    const url = await uploadFile(file);
    if (url) { form[field] = url; showSuccess('Imagen cargada'); }
  } finally {
    isUploading1.value = false; isUploading2.value = false;
  }
};

// --- LÓGICA DE MONTO AUTOMÁTICO PARA FIDUCIARIA ---
watch(() => [form.collateral_type, form.used_amount], ([type, used]) => {
  if (type === 'FIDUCIARIA') {
    form.guarantee_amount = used;
    form.coverage_percentage = 100;
  }
});

watch(() => form.loan_id, (newVal) => {
  const selectedLoan = loans.value.find(l => l.id === newVal);
  if (selectedLoan) {
    form.used_amount = Number(selectedLoan.amount) || 0;
    if (selectedLoan.customer_id === form.guarantor_id) clearGuarantor();
  }
});

watch(() => [form.guarantee_amount, form.used_amount], ([guarantee, used]) => {
  if (form.collateral_type !== 'FIDUCIARIA') {
    form.coverage_percentage = used > 0 && guarantee > 0 ? Number(((guarantee / used) * 100).toFixed(2)) : 0;
  }
});

watch(() => [form.start_date, form.end_date], ([start, end]) => {
  dateError.value = null;
  if (end) {
    const endDate = new Date(end);
    endDate.setMinutes(endDate.getMinutes() + endDate.getTimezoneOffset());
    const today = new Date(); today.setHours(0,0,0,0);
    if (endDate <= today) dateError.value = "Fecha de fin debe ser mayor a hoy.";
  }
  if (start && end) {
    const d1 = new Date(start); const d2 = new Date(end);
    let months = (d2.getFullYear() - d1.getFullYear()) * 12 - d1.getMonth() + d2.getMonth();
    form.term_months = months > 0 ? months : 0;
  }
});

const handleSubmit = async () => {
  if (dateError.value) return showError("Corrige las fechas.");
  if (form.collateral_type === 'FIDUCIARIA') {
    if (!form.guarantor_id) return showError("Debe seleccionar un fiador.");

    // Convertimos ambos a Number para evitar el error de 4 vs '4'
    if (Number(form.guarantor_id) === Number(selectedLoanCustomerId.value)) {
      clearGuarantor();
      return showError("El cliente del crédito no puede ser su propio fiador.");
    }

    // Forzamos que el monto sea el total del crédito
    form.guarantee_amount = form.used_amount;
  }
  isSaving.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const payload = { ...form, company_id: user.company };
    if (form.id) await apiClient.put(`/collaterals/${form.id}`, payload);
    else { delete payload.id; await apiClient.post('/collaterals', payload); }
    showSuccess('Garantía guardada'); emit('saved'); emit('close');
  } catch (e) { showError('Error al guardar'); } finally { isSaving.value = false; }
};
</script>